import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { ROLES } from '../../data/store';
import { BillFormatTab } from './BillFormatTab';
import { BranchDesignsTab } from './BranchDesignsTab';
import { Printer, Save, Bluetooth, Wifi, Usb, CheckCircle2, AlertCircle } from 'lucide-react';
import { connectBluetooth, disconnectBluetooth, forgetBluetoothPrinter, getBluetoothStatus, describeBluetoothError, isSilentReconnectSupported, getBluetoothDeviceName, getLastReconnectReport, getBluetoothCapabilities, wantsBluetoothConnected, startBluetoothSupervisor, getRememberedPrinterName } from '../../utils/printer';

export const PrinterTab = ({ org, role, showNotification }) => {
  // Only the organization admin decides what a bill looks like. A branch has
  // its own printer to set up, so Connection stays open to everyone; letting a
  // branch edit designs or reassign its own would mean each shop printing
  // whatever it liked, which is the opposite of why designs are set centrally.
  const canEditDesigns = role === ROLES.ORG_ADMIN;
  // Connection setup and receipt design are separate jobs done at different
  // times — one when the hardware is installed, one whenever the shop wants a
  // different bill. Same tab bar as OrgAdminProfile so the pages behave alike.
  const [tab, setTab] = useState('connection');
  const [method, setMethod] = useState('browser');
  const [ip, setIp] = useState('');
  const [port, setPort] = useState('9100');
  const [btStatus, setBtStatus] = useState('disconnected'); // 'disconnected' | 'connecting' | 'connected'
  const [wifiMode, setWifiMode] = useState(null);
  const [diagnosticMsg, setDiagnosticMsg] = useState(null);
  // Whether this browser can reconnect a paired printer without the chooser.
  const [silentReconnect] = useState(() => isSilentReconnectSupported());
  // How many printers this browser still remembers. Shown on screen because it
  // is the one fact that explains a failed reconnect, and reading it from the
  // developer console is not something a shop owner can be asked to do.
  const [rememberedCount, setRememberedCount] = useState(null);
  // True only while the user's own connect attempt is in flight. The background
  // reconnect also sets btStatus to 'connecting', and that runs for 20 seconds —
  // disabling the buttons on btStatus alone locked the user out for the whole
  // window, exactly when they might want to act.
  const [manualBusy, setManualBusy] = useState(false);
  // Which printer the card names. Falls back to the remembered device so the
  // card can say what it will connect to before it has connected.
  const [deviceName, setDeviceName] = useState(null);
  // Names of every printer this browser remembers. Kept as a list because a
  // shop may pair more than one: naming devices[0] would be picking whichever
  // the browser happened to list first, which is not necessarily theirs.
  const [rememberedNames, setRememberedNames] = useState([]);
  // What the last automatic reconnect actually did. Polled, because the attempt
  // runs in the background and finishes long after this effect has returned.
  const [reconnectReport, setReconnectReport] = useState(null);
  // Read once: these do not change while the page is open.
  const [caps] = useState(() => getBluetoothCapabilities());
  
  useEffect(() => {
    const savedPrinter = localStorage.getItem('org.device.printer');
    if (savedPrinter) {
      try {
        const parsed = JSON.parse(savedPrinter);
        if (parsed.method) setMethod(parsed.method);
        if (parsed.ip) setIp(parsed.ip);
        if (parsed.port) setPort(parsed.port);
      } catch (e) {
        console.error('Failed to parse saved printer', e);
      }
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    const initBt = async () => {
      if (getBluetoothStatus()) {
        setBtStatus('connected');
        setDeviceName(getBluetoothDeviceName() || getRememberedPrinterName() || null);
        return;
      }

      const remembered = getRememberedPrinterName();
      if (remembered) {
        setDeviceName(remembered);
      }

      if (!navigator.bluetooth || !navigator.bluetooth.getDevices) {
        setBtStatus('disconnected');
        return;
      }

      let devices = [];
      try {
        devices = await navigator.bluetooth.getDevices();
        if (!cancelled) {
          setRememberedCount(devices.length);
          setRememberedNames(devices.map((d) => d.name).filter(Boolean));
        }
      } catch {
        if (!cancelled) setRememberedCount(null);
      }

      if (devices.length === 0) {
        setBtStatus('disconnected');
        return;
      }

      if (wantsBluetoothConnected()) {
        setBtStatus('connecting');
        startBluetoothSupervisor();
        // Give background auto-reconnect 3.5s; if not connected, show disconnected so user can click Reconnect
        setTimeout(() => {
          if (!cancelled && !getBluetoothStatus()) {
            setBtStatus('disconnected');
          }
        }, 3500);
      } else {
        setBtStatus('disconnected');
      }
    };

    initBt();

    const reportPoll = setInterval(() => {
      setReconnectReport(getLastReconnectReport());
      if (getBluetoothStatus()) {
        setBtStatus('connected');
        setDeviceName(getBluetoothDeviceName() || getRememberedPrinterName() || null);
      }
    }, 1000);

    const handleConnect = () => {
      setBtStatus('connected');
      setDeviceName(getBluetoothDeviceName() || getRememberedPrinterName() || null);
    };
    const handleDisconnect = () => setBtStatus('disconnected');

    window.addEventListener('org-printer-bt-connected', handleConnect);
    window.addEventListener('org-printer-bt-disconnected', handleDisconnect);

    return () => {
      cancelled = true;
      clearInterval(reportPoll);
      window.removeEventListener('org-printer-bt-connected', handleConnect);
      window.removeEventListener('org-printer-bt-disconnected', handleDisconnect);
    };
  }, []);

  const handleSetActiveMethod = () => {
    // Save to complex object
    localStorage.setItem('org.device.printer', JSON.stringify({ method, ip, port }));
    // Save to the simple preferred key used by BillingTab
    localStorage.setItem('preferredPrinterMethod', method);
    
    // Dispatch global event for BillingTab to listen
    window.dispatchEvent(new CustomEvent('org-printer-method-changed', { detail: { method } }));
    
    showNotification(`Set active printer method to ${method.toUpperCase()}`, 'success');
  };

  const handleSave = () => {
    localStorage.setItem('org.device.printer', JSON.stringify({ method, ip, port }));
    // Also save legacy compatibility key if needed by older components
    if (method === 'wifi') localStorage.setItem('printerWifiIp', ip);
    localStorage.setItem('preferredPrinterMethod', method);
    
    showNotification('Printer configuration saved successfully.', 'success');
  };

  const handleConnectBluetooth = async () => {
    setManualBusy(true);
    setBtStatus('connecting');
    try {
      await connectBluetooth();
      setBtStatus('connected');
      setDeviceName(getBluetoothDeviceName() || deviceName);
      showNotification('Bluetooth printer connected successfully!', 'success');
    } catch (e) {
      console.error(e);
      setBtStatus('disconnected');
      showNotification(describeBluetoothError(e), 'error');
    } finally {
      setManualBusy(false);
    }
  };

  const handleDisconnectBluetooth = () => {
    // The only intentional stop. This clears the stored intent, so nothing
    // reconnects afterwards until the user asks for it.
    disconnectBluetooth();
    setBtStatus('disconnected');
    showNotification('Bluetooth printer disconnected. It will stay disconnected until you connect it again.', 'success');
  };

  const handleForgetBluetooth = async () => {
    try {
      await forgetBluetoothPrinter();
      setDeviceName(null);
      setRememberedCount(0);
      setRememberedNames([]);
      setBtStatus('disconnected');
      showNotification('Bluetooth printer removed.', 'success');
    } catch (e) {
      console.error(e);
      showNotification('Could not remove printer: ' + (e?.message || e), 'error');
    }
  };

  const handleTestUSB = async () => {
    try {
      const { printViaUSB, buildReceiptData } = await import('../../utils/printer');
      const testBill = {
        id: 'TEST-USB',
        date: new Date().toISOString(),
        customer: 'System Admin',
        method: 'usb',
        lines: [{ name: 'USB Connection Test', qty: 1, lineTotal: 0 }]
      };
      const org = { name: 'Mitra POS - SETUP' };
      const bytes = buildReceiptData(org, testBill);
      
      await printViaUSB(bytes);
      showNotification('Test print sent to USB printer successfully!', 'success');
    } catch (e) {
      console.error(e);
      showNotification('USB Error: ' + e.message, 'error');
    }
  };

  const handleTestPrint = async () => {
    if (!ip) {
      showNotification('Please enter a printer IP address.', 'error');
      return;
    }
    setDiagnosticMsg({ type: 'loading', text: 'Checking connection to ' + ip + '...' });
    try {
      const { fetchWithAuth } = await import('../../utils/api');
      const res = await fetchWithAuth(`/print/wifi/check?ip=${ip}`);
      
      const resData = await res.json().catch(() => ({}));

      if (!res.ok) {
        setDiagnosticMsg({ type: 'error', text: resData.error || 'Failed to connect.' });
        return;
      }
      
      setDiagnosticMsg({ type: 'success', text: 'Connection successful! Attempting test print...' });

      const { printViaWifi, buildReceiptData } = await import('../../utils/printer');
      const testBill = {
        id: 'TEST-WIFI',
        date: new Date().toISOString(),
        customer: 'System Admin',
        method: 'wifi',
        lines: [{ name: 'Wi-Fi Connection Test', qty: 1, lineTotal: 0 }]
      };
      const org = { name: 'Mitra POS - SETUP' };
      const bytes = buildReceiptData(org, testBill);
      
      await printViaWifi(bytes, ip);
      showNotification('Test print request sent successfully.', 'success');
      setDiagnosticMsg(null);
    } catch (e) {
      showNotification(e.message || 'Failed to send test print.', 'error');
      setDiagnosticMsg({ type: 'error', text: e.message || 'Print failed after connection.' });
    }
  };

  const tabClass = (id) =>
    `px-4 py-3 text-xs font-bold uppercase tracking-[0.1em] transition-all border-b-2 -mb-[1px] ${
      tab === id
        ? 'border-[var(--theme-primary)] text-[var(--theme-primary)]'
        : 'border-transparent text-slate-400 hover:text-slate-600'
    }`;

  return (
    <div className="w-full h-full flex flex-col space-y-6">

      <div className="bg-white rounded-2xl shadow-sm border border-slate-200 p-8 flex-1">
        <div className="flex items-center gap-6 border-b border-gray-100 -mt-2 mb-6">
          <button type="button" onClick={() => setTab('connection')} className={tabClass('connection')}>
            Connection
          </button>
          {canEditDesigns && (
            <>
              <button type="button" onClick={() => setTab('format')} className={tabClass('format')}>
                Bill Format
              </button>
              <button type="button" onClick={() => setTab('branches')} className={tabClass('branches')}>
                Branches
              </button>
            </>
          )}
        </div>

        {canEditDesigns && tab === 'format' ? (
          <BillFormatTab org={org} showNotification={showNotification} />
        ) : canEditDesigns && tab === 'branches' ? (
          <BranchDesignsTab org={org} showNotification={showNotification} />
        ) : (
        <div className="w-full space-y-8">
          <div className="pb-6 border-b border-slate-100">
            <h3 className="text-lg font-bold text-slate-800 mb-2">Hardware Connection Strategy</h3>
            <p className="text-sm text-slate-500">
              Configure your preferred hardware receipt printer connection method. This setting applies only to this specific device (browser). If you use multiple devices, configure them individually.
            </p>
          </div>

          <div className="pt-2">
            <label className="block text-xs font-bold tracking-widest uppercase text-slate-400 mb-4">
              Connection Method
            </label>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <button
                onClick={() => setMethod('wifi')}
                className={`flex flex-row items-center justify-center gap-2 py-3 px-4 rounded-xl border transition-colors ${
                  method === 'wifi' 
                    ? 'border-[var(--theme-primary)] bg-[var(--theme-primary-soft)] text-[var(--theme-primary)]' 
                    : 'border-slate-200 bg-white text-slate-500 hover:bg-slate-50'
                }`}
              >
                <Wifi size={20} />
                <span className="text-sm font-semibold">Wi-Fi</span>
              </button>
              <button
                onClick={() => setMethod('bluetooth')}
                className={`flex flex-row items-center justify-center gap-2 py-3 px-4 rounded-xl border transition-colors ${
                  method === 'bluetooth' 
                    ? 'border-[var(--theme-primary)] bg-[var(--theme-primary-soft)] text-[var(--theme-primary)]' 
                    : 'border-slate-200 bg-white text-slate-500 hover:bg-slate-50'
                }`}
              >
                <Bluetooth size={20} />
                <span className="text-sm font-semibold">Bluetooth</span>
              </button>
              <button
                onClick={() => setMethod('usb')}
                className={`flex flex-row items-center justify-center gap-2 py-3 px-4 rounded-xl border transition-colors ${
                  method === 'usb' 
                    ? 'border-[var(--theme-primary)] bg-[var(--theme-primary-soft)] text-[var(--theme-primary)]' 
                    : 'border-slate-200 bg-white text-slate-500 hover:bg-slate-50'
                }`}
              >
                <Usb size={20} />
                <span className="text-sm font-semibold">USB</span>
              </button>
            </div>
          </div>
          
          <div className="h-px bg-slate-100 my-4" />

          {method === 'wifi' && (
            <motion.div initial={{ opacity: 0, y: 5 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
              <div className="space-y-3">
                <p className="text-sm font-bold text-slate-700">What does your printer's test receipt say for "Wifi Mode"?</p>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 cursor-pointer bg-slate-50 border border-slate-200 px-4 py-2 rounded-lg hover:bg-blue-50 transition-colors">
                    <input type="radio" name="wifiMode" checked={wifiMode === 'AP'} onChange={() => setWifiMode('AP')} className="w-4 h-4 text-blue-600" />
                    <span className="text-sm font-semibold">Mode: AP</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer bg-slate-50 border border-slate-200 px-4 py-2 rounded-lg hover:bg-blue-50 transition-colors">
                    <input type="radio" name="wifiMode" checked={wifiMode === 'STA'} onChange={() => setWifiMode('STA')} className="w-4 h-4 text-blue-600" />
                    <span className="text-sm font-semibold">Mode: STA</span>
                  </label>
                </div>
              </div>

              <AnimatePresence>
                {wifiMode === 'AP' && (
                  <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="overflow-hidden">
                    <div className="p-4 bg-red-50 border border-red-200 rounded-xl text-sm text-red-800 space-y-2">
                      <p className="font-bold flex items-center gap-2"><AlertCircle size={16} /> Internet Warning</p>
                      <p>
                        To use <strong>AP Mode</strong>, you must connect this POS computer to the printer&apos;s own Wi-Fi network. Its name is printed on the printer&apos;s self-test page.<br/><br/>
                        <strong>⚠️ You will lose your regular internet connection while connected to the printer!</strong><br/>
                        We strongly recommend using <strong>USB</strong> or <strong>Bluetooth</strong> instead so you can keep your internet connection active.
                      </p>
                    </div>
                  </motion.div>
                )}
                {wifiMode === 'STA' && (
                  <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }} className="overflow-hidden">
                    <div className="p-4 bg-green-50 border border-green-200 rounded-xl text-sm text-green-800 space-y-2">
                      <p className="font-bold flex items-center gap-2"><CheckCircle2 size={16} /> STA Mode Setup</p>
                      <p>Great! Ensure your POS computer and the printer are connected to the same normal office Wi-Fi network.</p>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-xs font-bold tracking-widest uppercase text-slate-400 mb-2">
                    Printer IP Address
                  </label>
                  <input
                    type="text"
                    className="w-full h-12 px-4 bg-slate-50 border border-slate-200 rounded-xl text-sm font-medium text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-900/10 focus:border-slate-400 transition-shadow"
                    placeholder="e.g. 192.168.1.100"
                    value={ip}
                    onChange={(e) => setIp(e.target.value)}
                  />
                </div>

                <div>
                  <label className="block text-xs font-bold tracking-widest uppercase text-slate-400 mb-2">
                    Printer Port
                  </label>
                  <input
                    type="text"
                    className="w-full h-12 px-4 bg-slate-50 border border-slate-200 rounded-xl text-sm font-medium text-slate-900 focus:outline-none focus:ring-2 focus:ring-slate-900/10 focus:border-slate-400 transition-shadow"
                    placeholder="e.g. 9100"
                    value={port}
                    onChange={(e) => setPort(e.target.value)}
                  />
                  <p className="text-[11px] font-medium text-slate-400 mt-2">Default is usually 9100</p>
                </div>
              </div>
              
              <div className="pt-2 space-y-4">
                <button
                  onClick={handleTestPrint}
                  className="w-full sm:w-auto h-10 px-6 text-sm flex items-center justify-center gap-2 bg-[#0d0d0d] text-white font-bold rounded-md hover:bg-black transition-all hover:scale-[1.02] shadow-sm hover:shadow-md"
                >
                  <Printer size={18} /> Test Network Print
                </button>

                <AnimatePresence>
                  {diagnosticMsg && (
                    <motion.div initial={{ opacity: 0, y: -5 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className={`p-4 rounded-xl border text-sm font-medium flex items-start gap-3 ${
                      diagnosticMsg.type === 'error' ? 'bg-red-50 border-red-200 text-red-700' :
                      diagnosticMsg.type === 'success' ? 'bg-green-50 border-green-200 text-green-700' :
                      'bg-blue-50 border-blue-200 text-blue-700'
                    }`}>
                      {diagnosticMsg.type === 'error' && <AlertCircle size={18} className="mt-0.5 shrink-0" />}
                      {diagnosticMsg.type === 'success' && <CheckCircle2 size={18} className="mt-0.5 shrink-0" />}
                      {diagnosticMsg.type === 'loading' && <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mt-0.5 shrink-0" />}
                      <div>{diagnosticMsg.text}</div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </motion.div>
          )}

          {method === 'bluetooth' && (
            <motion.div initial={{ opacity: 0, y: 5 }} animate={{ opacity: 1, y: 0 }} className="space-y-3">
              <p className="text-xs font-medium text-slate-500">Connect to a Bluetooth thermal printer.</p>

              {/* One card: state, which printer, and the single action. The panel
                  previously split these across a large notice box, a separate
                  status strip and two buttons that did the same job — Connect
                  already attempts a silent reconnect before offering the chooser,
                  so a second "reconnect" button had nothing of its own to do. */}
              {/* Deliberately plain: a bordered row with a status dot and one
                  action. An earlier version carried a coloured accent bar down
                  the left edge, which read as a warning stripe rather than a
                  status and fought with the tab chrome around it. The dot alone
                  carries the state. */}
              <div className="rounded-lg border border-[var(--theme-tertiary)] bg-white px-4 py-3 flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0">
                  <span className="relative flex h-2.5 w-2.5 shrink-0" aria-hidden="true">
                    {btStatus === 'connecting' && (
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75" />
                    )}
                    <span
                      className="relative inline-flex rounded-full h-2.5 w-2.5"
                      style={{
                        background: btStatus === 'connected'
                          ? 'var(--theme-primary)'
                          : btStatus === 'connecting' ? '#f59e0b' : '#cbd5e1',
                      }}
                    />
                  </span>
                  <div className="min-w-0">
                    <p className="text-[13px] font-bold tracking-tight text-[#0d0d0d]">
                      {btStatus === 'connected'
                        ? 'Connected'
                        : btStatus === 'connecting' ? 'Looking for your printer…' : 'Not connected'}
                    </p>
                    <p className="text-[11px] font-medium text-slate-500 truncate">
                      {/* Once connected, name the printer actually in use.
                          Otherwise name the remembered one only when there is
                          exactly one — with several paired, say how many rather
                          than guessing which. */}
                      {deviceName
                        || (rememberedNames.length === 1 ? rememberedNames[0] : null)
                        || getRememberedPrinterName()
                        || (rememberedCount === 0
                          ? 'No printer paired yet'
                          : rememberedCount > 1
                            ? `${rememberedCount} printers paired`
                            : 'Bluetooth thermal printer')}
                    </p>
                  </div>
                </div>

                <div className="flex items-center gap-2 shrink-0">
                  {getRememberedPrinterName() && (
                    <button
                      type="button"
                      onClick={handleForgetBluetooth}
                      disabled={manualBusy}
                      title="Forget this printer and remove it"
                      className="h-9 px-3 text-xs font-semibold rounded-lg text-slate-400 hover:text-rose-600 hover:bg-rose-50 transition-colors disabled:opacity-50"
                    >
                      Remove
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={btStatus === 'connected' ? handleDisconnectBluetooth : handleConnectBluetooth}
                    disabled={manualBusy}
                    className={`h-9 px-5 text-xs font-bold uppercase tracking-[0.12em] rounded-lg shrink-0 transition-all disabled:opacity-50 ${
                      btStatus === 'connected'
                        ? 'border border-[var(--theme-tertiary)] text-slate-600 hover:bg-slate-50'
                        : 'text-white shadow-sm hover:opacity-90'
                    }`}
                    style={btStatus === 'connected' ? undefined : { background: 'var(--theme-primary)' }}
                  >
                    {manualBusy ? 'Working…' : btStatus === 'connected' ? 'Disconnect' : getRememberedPrinterName() ? 'Reconnect' : 'Connect'}
                  </button>
                </div>
              </div>

              {reconnectReport && !reconnectReport.ok && btStatus !== 'connected' && (
                <p className="text-[11px] font-medium text-rose-600">
                  Last automatic attempt at {reconnectReport.at} failed after{' '}
                  {reconnectReport.tries} {reconnectReport.tries === 1 ? 'try' : 'tries'}: {reconnectReport.reason}
                </p>
              )}

              {/* Blunt capability readout. "It doesn't work" cannot be diagnosed;
                  "getDevices missing on Edge 152" can, and the user can read this
                  back verbatim without opening developer tools. */}
              <p className="text-[10px] font-mono text-slate-400">
                {caps.browser}
                {' · '}bluetooth {caps.hasBluetooth ? '✓' : '✗'}
                {' · '}getDevices {caps.hasGetDevices ? '✓' : '✗'}
                {' · '}watchAdvertisements {caps.hasWatchAdvertisements ? '✓' : '✗'}
                {' · '}secure {caps.secureContext ? '✓' : '✗'}
              </p>

              {silentReconnect ? (
                <p className="text-[11px] font-medium text-slate-500">
                  Reconnects automatically after a refresh
                  {rememberedCount > 0 ? ' — this browser remembers your printer.' : '. Pair once to enable it.'}
                </p>
              ) : (
                <div className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-[11px] text-amber-900">
                  <p className="font-bold">One click to reconnect after a refresh</p>
                  <p className="pt-0.5">
                    {caps.browser.startsWith('Edge')
                      ? 'Edge cannot hand a saved printer back to this page, so it has to ask each time. There is no setting that changes this.'
                      : 'This browser cannot return a saved printer to the page, so it has to ask each time.'}
                    {getRememberedPrinterName()
                      ? ` Press Connect and pick ${getRememberedPrinterName()} — it is the only device shown.`
                      : ' Press Connect and choose your printer.'}
                  </p>
                  <p className="pt-1">
                    For fully automatic reconnection, use <span className="font-bold">Google Chrome</span>,
                    or print over <span className="font-bold">USB</span> or <span className="font-bold">Wi-Fi</span>,
                    which work in every browser.
                  </p>
                </div>
              )}
            </motion.div>
          )}

          {method === 'usb' && (
            <motion.div initial={{ opacity: 0, y: 5 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
              <div className="p-5 bg-amber-50 border border-amber-200 rounded-xl text-sm text-amber-800 space-y-2">
                <p className="font-bold flex items-center gap-2"><Usb size={16} /> WebUSB Connection Guide</p>
                <ul className="list-disc pl-5 space-y-1 mt-2 text-amber-900/80">
                  <li>Your browser must support WebUSB (Chrome, Edge).</li>
                  <li>On Windows, you may need to use <a href="https://zadig.akeo.ie/" target="_blank" rel="noreferrer" className="underline font-bold">Zadig</a> to replace the printer driver with WinUSB if you get access denied errors.</li>
                </ul>
              </div>
              <div className="pt-2">
                <button
                  onClick={handleTestUSB}
                  className="w-full sm:w-auto h-10 px-6 text-sm flex items-center justify-center gap-2 bg-[#0d0d0d] text-white font-bold rounded-md hover:bg-black transition-all hover:scale-[1.02] shadow-sm hover:shadow-md"
                >
                  <Usb size={18} /> Pair & Test USB Printer
                </button>
              </div>
            </motion.div>
          )}

          <div className="pt-8 border-t border-slate-100 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="text-sm font-medium text-slate-500">
              Active Method: <span className="font-bold text-slate-900 uppercase">{(localStorage.getItem('preferredPrinterMethod') || 'Not Set')}</span>
            </div>
            <button
              onClick={handleSetActiveMethod}
              className="w-full sm:w-auto h-10 px-6 text-sm flex items-center justify-center gap-2 bg-[var(--theme-primary)] text-white font-bold rounded-md shadow-sm hover:opacity-90 transition-all hover:scale-[1.02]"
            >
              <Save size={18} /> Set as Active Method
            </button>
          </div>
        </div>
        )}
      </div>
    </div>
  );
};

PrinterTab.propTypes = {
  org: PropTypes.object.isRequired,
  role: PropTypes.string,
  showNotification: PropTypes.func,
};
